import React, { useState, useEffect } from "react";
import InvoiceField from "../component/InvoiceFiled";

const MedicalForm = ({ data = {}, onUpdate = () => {} }) => {
  // Default initial data
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: "",
    date: "",
    clientName: "",
    location: "",
    preparedBy: "",
    dueDate: "",
    paymentTerms: "Due on receipt",
    salesTax: 18,
    items: [
      { months: 1, desc: "", rate: 0, amount: 0 },
    ],
    ...data,
  });

  // Update function for main form fields
  const handleFieldChange = (field, value) => {
    const updated = { ...invoiceData, [field]: value };
    setInvoiceData(updated);
    onUpdate(updated);
  };

  // Update function for items
  const handleItemChange = (index, field, value) => {
    const itemsCopy = [...invoiceData.items];
    itemsCopy[index][field] = value;

    // Auto-calculate amount
    const months = Number(itemsCopy[index].months || 0);
    const rate = Number(itemsCopy[index].rate || 0);
    itemsCopy[index].amount = (months * rate).toFixed(2);

    const updated = { ...invoiceData, items: itemsCopy };
    setInvoiceData(updated);
    onUpdate(updated);
  };

  // Add a new item row
  const addItem = () => {
    const updated = {
      ...invoiceData,
      items: [...invoiceData.items, { months: 1, desc: "", rate: 0, amount: 0 }],
    };
    setInvoiceData(updated);
    onUpdate(updated);
  };

  // Remove an item row
  const removeItem = (index) => {
    const itemsCopy = invoiceData.items.filter((_, i) => i !== index);
    const updated = { ...invoiceData, items: itemsCopy.length ? itemsCopy : [{ months: 1, desc: "", rate: 0, amount: 0 }] };
    setInvoiceData(updated);
    onUpdate(updated);
  };

  // Calculate subtotal, GST, total
  const subtotal = invoiceData.items.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const gstRate = Number(invoiceData.salesTax || 0);
  const gstAmount = (subtotal * gstRate) / 100;
  const total = subtotal + gstAmount;

  return (
    <div className="space-y-6">
      <h3 className="font-bold text-lg text-[#2D5A27] border-b pb-2 uppercase tracking-wide">
        Medical Aesthetics Billing
      </h3>

      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <InvoiceField
          label="Invoice #"
          value={invoiceData.invoiceNumber}
          onChange={(v) => handleFieldChange("invoiceNumber", v)}
        />
        <InvoiceField
          label="Date"
          type="date"
          value={invoiceData.date}
          onChange={(v) => handleFieldChange("date", v)}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <InvoiceField
          label="Patient Name"
          value={invoiceData.clientName}
          onChange={(v) => handleFieldChange("clientName", v)}
        />
        <InvoiceField
          label="Treatment Location"
          value={invoiceData.location}
          onChange={(v) => handleFieldChange("location", v)}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <InvoiceField
          label="Invoice Prepared By"
          value={invoiceData.preparedBy}
          onChange={(v) => handleFieldChange("preparedBy", v)}
        />
        <InvoiceField
          label="Due Date"
          type="date"
          value={invoiceData.dueDate}
          onChange={(v) => handleFieldChange("dueDate", v)}
        />
      </div>

      <InvoiceField
        label="Payment Terms"
        type="dropdown"
        value={invoiceData.paymentTerms}
        options={["Due on receipt", "Net 15", "Net 30"]}
        onChange={(v) => handleFieldChange("paymentTerms", v)}
      />

      <InvoiceField
        label="GST (%)"
        type="number"
        value={invoiceData.salesTax}
        onChange={(v) => handleFieldChange("salesTax", v)}
      />

      {/* Items Table */}
      <div className="overflow-x-auto border rounded">
        <table className="w-full text-xs">
          <thead className="bg-[#7DB164] text-white">
            <tr>
              <th className="p-2 w-12">#</th>
              <th className="p-2 text-left">Description</th>
              <th className="p-2 w-20">Months</th>
              <th className="p-2 w-24">Unit Price (₹)</th>
              <th className="p-2 w-24">Amount (₹)</th>
              <th className="p-2 w-20">Action</th>
            </tr>
          </thead>
          <tbody>
            {invoiceData.items.map((item, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                <td className="text-center">{idx + 1}</td>
                <td>
                  <InvoiceField
                    value={item.desc}
                    onChange={(v) => handleItemChange(idx, "desc", v)}
                  />
                </td>
                <td>
                  <InvoiceField
                    type="number"
                    value={item.months}
                    onChange={(v) => handleItemChange(idx, "months", v)}
                  />
                </td>
                <td>
                  <InvoiceField
                    type="number"
                    value={item.rate}
                    onChange={(v) => handleItemChange(idx, "rate", v)}
                  />
                </td>
                <td className="text-right font-medium">₹ {item.amount}</td>
                <td className="text-center">
                  <button
                    className="text-red-500 font-bold"
                    onClick={() => removeItem(idx)}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          className="mt-2 px-3 py-1 bg-green-600 text-white text-xs rounded"
          onClick={addItem}
        >
          + Add Item
        </button>
      </div>

      {/* Totals */}
      <div className="flex justify-end space-y-1 flex-col w-48 mt-4 text-xs font-bold">
        <div className="flex justify-between">
          <span>Subtotal</span>
          <span>₹ {subtotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>GST ({gstRate}%)</span>
          <span>₹ {gstAmount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-lg">
          <span>Total</span>
          <span>₹ {total.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
};

export default MedicalForm;
